/**
 * userFacingMessage — keep engineering text out of user-visible copy.
 *
 * VRG-607. A toast said, to a paying customer, mid-checkout:
 *
 *     Payment failed
 *     Request failed with status code 400
 *
 * The second line is axios's `error.message`. The call site was written as
 * `error?.message ?? 'Unable to update the subscription.'`, so the human
 * sentence was never reachable — `error.message` is always populated, and `??`
 * only falls through on null/undefined. Good copy sitting behind a `??` that
 * can't fire is the shape to watch for; it reads as handled and isn't.
 *
 * WHY A SANITISER AND NOT N CALL-SITE FIXES. There are ~34 toast call sites in
 * apps/app and ~350 snackbar usages in apps/platform. Editing the leaking ones
 * fixes today and not tomorrow — the same opt-in-that-everyone-forgets failure
 * working-conventions §42 records, where 35 of 37 call sites skipped a prop
 * they were supposed to pass. Both apps funnel every toast through a single
 * provider, so one filter there cannot be forgotten by a new call site.
 *
 * It is a BACKSTOP, not a licence. A caller that knows what went wrong should
 * still write a specific sentence — this only guarantees the floor.
 *
 * DELIBERATELY NOT A STATUS-CODE MAP. Mapping 4xx/5xx to copy needs the
 * response, which a toast provider does not have. This works on the string
 * alone, which is the last point where every path converges.
 */

/**
 * Strings that are engineering artefacts, never product copy.
 *
 * Ordered roughly by how often each has actually been observed reaching a user
 * in this repo. The axios and NestJS defaults are not hypothetical: the axios
 * one shipped (above), and `useGetCertificates.ts` documents "Forbidden
 * resource" and "Internal server error" reaching customers.
 */
const TECHNICAL_SIGNALS: RegExp[] = [
  // axios: "Request failed with status code 400"
  /request failed with status code/i,
  // any bare status reference: "status 500", "status code 404", "HTTP 502"
  /\bstatus\s*(code)?\s*[1-5]\d{2}\b/i,
  /\bhttp\s*[1-5]\d{2}\b/i,
  // NestJS built-in exception defaults, verbatim
  /^(unauthorized|forbidden resource|internal server error|bad request|not found|conflict|unprocessable entity)$/i,
  // NestJS 404 route default: "Cannot POST /auth/customer/2fa/totp/enroll"
  /\bcannot (get|post|put|patch|delete|head|options)\s+\//i,
  // fetch/network layer
  /\bnetwork error\b/i,
  /\bfailed to fetch\b/i,
  // axios timeout: "timeout of 30000ms exceeded". Caught by test rather than by
  // reading the axios source — worth keeping as evidence that this list is
  // observed, not imagined.
  /\btimeout of \d+ms exceeded\b/i,
  /\btimed? ?out after \d+/i,
  /\bERR_[A-Z_]+\b/,
  /\b(ECONNREFUSED|ETIMEDOUT|ENOTFOUND|ECONNRESET|EAI_AGAIN)\b/,
  // JS error plumbing
  /\b(TypeError|ReferenceError|SyntaxError|RangeError|AxiosError|FetchError)\b/,
  // A stack frame. Keyed on the `:line:column)` signature rather than on the
  // frame's NAME: the first version required `at \w+ (`, which cannot span
  // `Object.<anonymous>` — dots and angle brackets are not word characters — so
  // it missed the single most common frame shape in Node. Caught by the guard
  // spec, which is the point of having one.
  /:\d+:\d+\)/,
  /\[object \w+\]/,
  // values that mean "a variable was empty", never a message
  /^(undefined|null|NaN|\{\}|\[\]|error|Error)$/i,
  // database / ORM
  /\b(violates|constraint|relation|column|duplicate key|deadlock|drizzle|postgres|sql)\b/i,
  // framework/validation placeholders that are not sentences
  /^validation errors?$/i,
  // GraphQL / provider plumbing
  /\bGraphQL\b/i,
  /\binvalid_(request|grant|client|token|scope)\b/i,
  // a bare path or URL as the whole message
  /^\/?[\w-]+(\/[\w:-]+)+\/?$/,
  /^https?:\/\//i,

  // ---- VRG-607 second pass: the leak this list did NOT catch ----
  //
  // A server-authored 4xx message reached a brand's screen reading:
  //
  //   Cannot create a Stripe customer for company f0d79e0e-3ff9-4d44-…
  //   ("Louis Vuitton"): it has no support_email and no admin with an email
  //   address. Stripe requires an email to issue invoices…
  //
  // Every signal above missed it, because it is fluent English with no status
  // code, no stack frame and no framework default. The patterns below are what
  // it DID carry, and they generalise: an identifier a human cannot act on, a
  // database column name, or a paragraph where a sentence belongs.
  //
  // A UUID is the strongest of the three. No user-facing sentence has ever
  // needed one — if an id must be shown (a support reference), it belongs in
  // its own labelled field, not welded into prose.
  /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/i,

  // snake_case identifiers: `support_email`, `stripe_customer_id`,
  // `company_id`. Product copy uses spaces. Two or more underscore-joined
  // lowercase words is a column or a variable, never a phrase someone typed
  // for a reader.
  /\b[a-z][a-z0-9]*(_[a-z0-9]+){1,}\b/,

  // A toast is one or two sentences. Forty words is a developer explaining
  // themselves, which is what the message above was. Length alone is weak
  // evidence, so this is deliberately set far above any real copy — the
  // longest legitimate string in this repo's toasts is ~20 words.
  /^\S+(\s+\S+){39,}$/,
]

/**
 * True when `text` looks like something an engineer wrote for another engineer.
 */
export const isTechnicalMessage = (text: unknown): boolean => {
  if (typeof text !== 'string') return false
  const trimmed = text.trim()
  if (!trimmed) return false
  return TECHNICAL_SIGNALS.some((pattern) => pattern.test(trimmed))
}

/**
 * The floor for a message with no usable copy of its own.
 *
 * Says what happened, gives one thing to try, and names support as the exit —
 * without a code to read out, because a user cannot act on one and it is the
 * exact thing this module exists to remove. Support can find the failure from
 * the timestamp and the account.
 */
export const GENERIC_ERROR_TEXT =
  'Something went wrong. Please try again, and contact support if it keeps happening.'

/**
 * Return `text` when it is safe to show, otherwise `fallback`.
 *
 * `fallback` is what the CALLER meant to say; it is used unless it is itself
 * technical, which is why the return is checked rather than trusted.
 */
export const userFacingMessage = (
  text: unknown,
  fallback: string = GENERIC_ERROR_TEXT,
): string => {
  if (typeof text === 'string' && text.trim() && !isTechnicalMessage(text)) {
    return text.trim()
  }
  return isTechnicalMessage(fallback) ? GENERIC_ERROR_TEXT : fallback
}
